import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Dropdown,
  DropdownItem,
  Flex,
  Heading,
  HelpTip,
  Link,
  Modal,
  Snackbar,
  Table,
  TabbedNavigation,
  TabbedNavigationItem,
  Text,
} from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import ConfirmationModal from '../../combineChoose/components/ConfirmationModal';
import { ModalFooterActions } from '../../combineChoose/style';
import { useCombineCompletion } from '../useCombineCompletion';
import { SNACKBAR_POSITION } from './style';
import AmazonAdsAgreementLink from '@features/combine-accounts/components/AmazonAdsAgreementLink';
import { DEFAULT_LOCALE } from '@i18n/utils';
import { convertLocale } from 'src/shared/utils/global';
import { selectLocale } from 'src/shared/state/redux';
import {
  useLazyGetCombinationInvitationsQuery,
  useUpdateCombinationInvitationMutation,
} from 'src/shared/services/combine-accounts/combineAccounts';
import {
  CombinationInvitationDirection,
  CombinationInvitationSummary,
  CombinationInvitationUpdateStatus,
} from 'src/shared/services/combine-accounts/types';
import {
  COMBINE_REQUESTS_ACTIONS_LABEL_KEY,
  COMBINE_ACCOUNTS_CANCEL_KEY,
  COMBINE_REQUESTS_ACTION_APPROVE_KEY,
  COMBINE_REQUESTS_CONFIRM_AGREEMENT_KEY,
  COMBINE_REQUESTS_CONFIRM_BODY_KEY,
  COMBINE_REQUESTS_CONFIRM_BULLET_ADMINS_KEY,
  COMBINE_REQUESTS_CONFIRM_BULLET_BILLING_KEY,
  COMBINE_REQUESTS_CONFIRM_BULLET_CAMPAIGNS_KEY,
  COMBINE_REQUESTS_CONFIRM_BULLET_FEATURES_KEY,
  COMBINE_REQUESTS_CONFIRM_BULLET_MANAGE_KEY,
  COMBINE_REQUESTS_CONFIRM_LOADING_KEY,
  COMBINE_REQUESTS_CONFIRM_SUBMIT_KEY,
  COMBINE_REQUESTS_CONFIRM_TITLE_KEY,
  COMBINE_REQUESTS_PROGRESS_SNACKBAR_KEY,
  COMBINE_REQUESTS_PROGRESS_SNACKBAR_ONE_KEY,
  COMBINE_REQUESTS_SUCCESS_ACCOUNT_SETTINGS_KEY,
  COMBINE_REQUESTS_SUCCESS_BODY_KEY,
  COMBINE_REQUESTS_SUCCESS_CAMPAIGNS_KEY,
  COMBINE_REQUESTS_SUCCESS_TITLE_KEY,
  COMBINE_REQUESTS_ACTION_CANCEL_KEY,
  COMBINE_REQUESTS_ACTION_DECLINE_KEY,
  COMBINE_REQUESTS_ACTION_ERROR_KEY,
  COMBINE_REQUESTS_ACTION_SEND_AGAIN_KEY,
  COMBINE_REQUESTS_COLUMN_ACTION_KEY,
  COMBINE_REQUESTS_COLUMN_DSP_ACCOUNT_KEY,
  COMBINE_REQUESTS_COLUMN_EXPIRES_ON_KEY,
  COMBINE_REQUESTS_COLUMN_REQUESTED_ON_KEY,
  COMBINE_REQUESTS_COLUMN_REQUEST_TYPE_KEY,
  COMBINE_REQUESTS_COLUMN_SA_ACCOUNT_KEY,
  COMBINE_REQUESTS_COLUMN_STATUS_KEY,
  COMBINE_REQUESTS_HEADING_KEY,
  COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_COMBINE_BODY_KEY,
  COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_COMBINE_HEADER_KEY,
  COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_INTRO_KEY,
  COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_UPGRADE_BODY_KEY,
  COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_UPGRADE_HEADER_KEY,
  COMBINE_REQUESTS_STATUS_APPROVED_KEY,
  COMBINE_REQUESTS_STATUS_PENDING_KEY,
  COMBINE_REQUESTS_TAB_RECEIVED_KEY,
  COMBINE_REQUESTS_TAB_SENT_KEY,
  COMBINE_REQUESTS_TYPE_COMBINE_KEY,
  COMBINE_REQUESTS_VIEW_CAMPAIGNS_KEY,
} from '@features/combine-accounts/constants';
import { ucmAllCampaignsUrl, userManagementUrl } from 'src/shared/routes';
import { Weblab } from 'src/shared/weblab/enum';
import { isWeblabLaunched } from 'src/shared/weblab/weblabUtils';

export const PENDING_REQUESTS_CARD_ID = 'combine-pending-requests';

/*
  The design gives this chip as 65x20. The height is reproduced by the padding and
  line height rather than set outright, and the width is left to the content: 65px
  measures the English "Pending", and these labels are translated into every locale
  the site ships, where a width fixed to English would clip them. Mirrors the chip
  already used on the billing summary, differing only in the fill.
*/
const PENDING_FILL = '#f5f3ef';
const APPROVED_FILL = '#e9faed';

const StatusPill = styled.span<{ $approved: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 8px;
  background: ${({ $approved }) => ($approved ? APPROVED_FILL : PENDING_FILL)};
  border-radius: 8px;
  font-size: 12px;
  line-height: 18px;
  color: #161d26;
`;

/**
 * Placeholder for a value the service did not supply.
 *
 * A symbol rather than copy, so it carries no Puff entry: there is nothing here for a translator to
 * decide, and every locale renders a dash the same way.
 */
export const ABSENT_VALUE = '\u2013';

/** The one status this console has designed copy for. Everything else is shown as reported. */
/*
  A stop on the read loop, not a page size. Reached only by a list far larger than one
  admin could accumulate, and stopping leaves the rows already read on screen rather
  than reading for ever against a service that kept answering with a token.
*/
const MAX_PAGES = 20;

const PENDING_STATUS = 'PENDING';
const APPROVED_STATUS = 'APPROVED';

/**
 * Formats a date for the table.
 *
 * `Intl` directly rather than Storm's `formatDate`, for two reasons. It renders a short month
 * ("Jan 31, 2026") with no way to ask for the long one the design specifies, and it formats in the
 * viewer's timezone, which moves a UTC midnight timestamp onto the previous day for anyone west of
 * UTC. An expiry that reads a day earlier than the service means it is worse than a formatting
 * mismatch, so the calendar date is pinned to UTC here.
 *
 * Returns the dash for an absent or unparseable value rather than throwing, since neither field is
 * supplied by the BFF yet and a bad value must not take the table down with it. That covers the
 * locale as well as the date: `selectLocale` hands back what Sauron said, which can be underscored
 * (`en_US`), and an underscore is not a valid language tag, so `Intl` rejects it outright. It is
 * normalised first so such a locale still formats, and the construction is guarded so any other
 * malformed tag degrades to the dash instead of throwing mid-render.
 */
export const formatRequestDate = (
  value: string | undefined,
  locale: string,
): string => {
  if (!value) {
    return ABSENT_VALUE;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return ABSENT_VALUE;
  }
  try {
    return new Intl.DateTimeFormat(convertLocale(locale), {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(parsed);
  } catch {
    return ABSENT_VALUE;
  }
};

/** One action offered on a row, and the status it sends. */
interface RowAction {
  labelKey: string;
  status: CombinationInvitationUpdateStatus;
}

/*
  Which actions a row offers depends on the side the viewer is on, because the two
  sides answer the same invitation differently: the counterparty accepts or refuses
  it, and the requester chases or withdraws it.

  Both refusals send REJECTED. GRS distinguishes them by caller identity rather
  than by status, revoking the requester's own request and declining anyone
  else's, so one status covers both and the copy is what differs.
*/
const RECEIVED_ACTIONS: RowAction[] = [
  {
    labelKey: COMBINE_REQUESTS_ACTION_APPROVE_KEY,
    status: CombinationInvitationUpdateStatus.APPROVED,
  },
  {
    labelKey: COMBINE_REQUESTS_ACTION_DECLINE_KEY,
    status: CombinationInvitationUpdateStatus.REJECTED,
  },
];

const SENT_ACTIONS: RowAction[] = [
  {
    labelKey: COMBINE_REQUESTS_ACTION_SEND_AGAIN_KEY,
    status: CombinationInvitationUpdateStatus.RESENT,
  },
  {
    labelKey: COMBINE_REQUESTS_ACTION_CANCEL_KEY,
    status: CombinationInvitationUpdateStatus.REJECTED,
  },
];

/**
 * Pending account-combination requests, in both directions, on the combine and upgrade landing page.
 *
 * <h3>Why it renders nothing so often</h3>
 *
 * The card removes itself when neither direction has a request, which is every ordinary visit: a
 * user who has raised no request and been sent none has nothing to be shown, and an empty table
 * would push the page's actual actions down the page to say so.
 *
 * It also renders nothing while the first read is in flight, so the card does not appear and then
 * vanish once the answer turns out to be empty, and nothing if that read fails. The card is
 * additive, and an error banner in place of a list the user did not ask for costs them the space
 * the page's primary actions occupy without telling them anything they can act on.
 *
 * <h3>What the service does not supply</h3>
 *
 * Both account names and both dates come from fields the BFF does not return yet, so those columns
 * render as dashes. `direction`, which splits the rows into the Sent and Received tabs, is supplied
 * by the BFF; GRS may not populate it, in which case it is absent and the row is shown under
 * Received.
 */
/*
  How long the dialog blocks on the answer before handing the wait over to the
  snackbar. Combining is asynchronous server-side, so there is no upper bound to
  wait for; past this point the user is better served by getting their page back.
*/
const HANDOFF_AFTER_MS = 10_000;

/*
  Width of the confirm dialog's body, not of the dialog.

  The approved design puts this dialog at 614px wide. Storm's Modal is
  `width: fit-content` with no width prop, so the number has to be expressed on the
  content, and the requester lane's 389px body producing its 429px dialog puts the
  surrounding chrome at 40px. Left unpinned, the acknowledgement sentence does not
  wrap until the modal's own `max-width: 80%`, so the dialog renders roughly twice
  its intended width on a wide window.
*/
const CONFIRM_BODY_WIDTH = '574px';

const PendingRequestsCard: FC = () => {
  const { intl } = useIntl();
  // Reads the configuration query, so it is called before any early return.
  const isFeatureLaunched = isWeblabLaunched(Weblab.COMBINE_REQUEST_ACCESS);
  // Falls back to the package default rather than the browser's, so a session
  // without a resolved locale formats dates the same way the rest of the app does.
  const locale = useSelector(selectLocale) ?? DEFAULT_LOCALE;

  const [fetchInvitations] = useLazyGetCombinationInvitationsQuery();
  const [updateInvitation] = useUpdateCombinationInvitationMutation();
  const awaitCombineCompletion = useCombineCompletion();

  const [rows, setRows] = useState<CombinationInvitationSummary[] | null>(null);
  const [actionFailed, setActionFailed] = useState(false);
  const [confirming, setConfirming] =
    useState<CombinationInvitationSummary | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /* Non-null while a combination is running with the dialog already closed. */
  const [combiningAccounts, setCombiningAccounts] = useState<number | null>(
    null,
  );
  const [combined, setCombined] = useState<CombinationInvitationSummary | null>(
    null,
  );
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  /*
    Reads every page before showing anything, and re-reads the whole list after an
    action.

    Paging this table by button cannot be made coherent. The service pages over the
    invitations as one list, while the tabs split them by direction on the client, so
    a "Load more" on the Sent tab could fetch a page of purely Received rows: nothing
    would arrive in the table the user was looking at, and the control would still be
    there afterwards. The counts in the tab labels have the same problem, reporting
    only what happened to be loaded rather than what exists.

    Paging per direction is not available either, because the response does not carry
    a direction at all. Reading the list out in full is what makes both the tables and
    their counts true, and it costs nothing here: these are one admin's outstanding
    invitations, which is also why answering one re-reads the lot.

    A tag invalidation is deliberately not used. It would refetch only the arguments
    last used, which is the tail rather than the list.
  */
  const loadAllPages = useCallback(async () => {
    try {
      const collected: CombinationInvitationSummary[] = [];
      let token: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const response = await fetchInvitations(
          token ? { paginationToken: token } : undefined,
        ).unwrap();
        collected.push(...response.accountCombinationInvitations);
        token = response.nextToken;
        if (!token) {
          break;
        }
      }
      setRows(collected);
      return collected;
    } catch {
      /*
        Recorded as an empty list, which hides the card by the same rule that hides
        it for a user with no requests. Nothing is known about whether the user has
        requests, so nothing is claimed.
      */
      setRows([]);
    }
  }, [fetchInvitations]);

  // Not read at all for a customer outside the treatment: the card cannot be shown,
  // so asking the service for its contents would be a request with no reader.
  useEffect(() => {
    if (isFeatureLaunched) {
      loadAllPages();
    }
  }, [isFeatureLaunched, loadAllPages]);

  const { received, sent } = useMemo(() => {
    const all = rows ?? [];
    return {
      /*
        Everything except an explicit SENT lands in Received. An invitation the
        viewer administers on both sides (SENT_AND_RECEIVED) and one whose
        direction the service did not report both show here rather than being
        dropped or shown in both tabs. Grouping both-sides under Received is safe
        and rare in practice, since an admin on both sides normally combines
        directly without a request; showing an unreported direction here rather
        than hiding the row is the lesser failure. Offering the received actions
        is safe either way: GRS validates the caller against the invitation, so
        an approval from someone who is not the counterparty is refused rather
        than misapplied.
      */
      received: all.filter(
        (entry) => entry.direction !== CombinationInvitationDirection.SENT,
      ),
      sent: all.filter(
        (entry) => entry.direction === CombinationInvitationDirection.SENT,
      ),
    };
  }, [rows]);

  /*
    Opens on the tab that has something in it, preferring received, so a user with
    one request does not land on an empty table and have to find the other tab.
    Set once, from the first read, so it does not jump under the user afterwards.
  */
  useEffect(() => {
    if (rows === null || selectedTab !== null) {
      return;
    }
    setSelectedTab(
      received.length > 0
        ? CombinationInvitationDirection.RECEIVED
        : CombinationInvitationDirection.SENT,
    );
  }, [rows, selectedTab, received.length]);

  const answer = useCallback(
    async (
      invitation: CombinationInvitationSummary,
      status: CombinationInvitationUpdateStatus,
    ) => {
      setActionFailed(false);
      try {
        await updateInvitation({
          accountCombinationInvitationId:
            invitation.accountCombinationInvitationId,
          status,
        }).unwrap();
        await loadAllPages();
      } catch {
        /*
          Reported as one failure without naming the reason. The service's own
          reasons for refusing an answer (already answered, not the caller's to
          answer, no longer pending) are not customer-facing copy, and every one of
          them is resolved the same way: read the refreshed list and look again.
        */
        setActionFailed(true);
        await loadAllPages();
      }
    },
    [updateInvitation, loadAllPages],
  );

  /*
    Approving is the one answer that starts work rather than just recording a
    decision, so it is the only one that goes through the dialog and the only one
    with a wait to report.

    The wait is owned here rather than by the dialog because it outlives it: at
    HANDOFF_AFTER_MS the dialog closes and the snackbar takes over, and the
    request is still in flight at that point. `handedOff` is a local rather than
    state so the resolution can tell which of the two it is completing, without
    reading state that may have changed since.
  */
  const handoffTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(handoffTimer.current), []);

  const approve = useCallback(
    async (invitation: CombinationInvitationSummary) => {
      setActionFailed(false);
      setSubmitting(true);
      let handedOff = false;
      const handoff = setTimeout(() => {
        handedOff = true;
        setSubmitting(false);
        setConfirming(null);
        setCombiningAccounts(invitation.accountIdsToCombine?.length ?? 0);
      }, HANDOFF_AFTER_MS);
      handoffTimer.current = handoff;

      const settle = () => {
        clearTimeout(handoff);
        setSubmitting(false);
        setConfirming(null);
        setCombiningAccounts(null);
      };

      try {
        await updateInvitation({
          accountCombinationInvitationId:
            invitation.accountCombinationInvitationId,
          status: CombinationInvitationUpdateStatus.APPROVED,
        }).unwrap();
        /*
          Acceptance is not completion: the service answers as soon as it has taken
          the answer, and combines the accounts afterwards. Waiting for the work
          itself is what makes the report that follows true, and what gives the
          handover above something to hand over.
        */
        const outcome = await awaitCombineCompletion(
          invitation.advertiserAccountId,
        );
        settle();

        /*
          Two ways this is a failure rather than a success. The combination finished and
          left the account worse than it found it, because the workflow recomputes the
          status near the end without regard for what it was before. Or nothing could be
          read at all, in which case saying nothing would leave someone with a request
          they answered and no report either way.
        */
        if (outcome === 'degraded' || outcome === 'unreadable') {
          setActionFailed(true);
          await loadAllPages();
          return;
        }

        /*
          The success dialog is only for a caller who waited it out and saw it finish.
          Raising it after the handover would interrupt a page they have already gone
          back to, and raising it when the watch merely gave up would claim an outcome
          nothing observed. The refreshed row is the report in both of those cases.
        */
        /*
          Refreshed before the dialog is raised, not after, because the dialog's two
          destinations are built from fields the service only supplies once the combined
          account has been created. Reading them off the row as it was before the
          combination would find them absent every time.
        */
        const refreshed = await loadAllPages();
        if (!handedOff && outcome === 'combined') {
          setCombined(
            refreshed?.find(
              (row) =>
                row.accountCombinationInvitationId ===
                invitation.accountCombinationInvitationId,
            ) ?? invitation,
          );
        }
      } catch {
        settle();
        setActionFailed(true);
        await loadAllPages();
      }
    },
    [updateInvitation, loadAllPages, awaitCombineCompletion],
  );

  const hasRequests = received.length > 0 || sent.length > 0;
  /*
    Nothing to show: the feature is not launched for this customer, the list is
    still being read, or the user has no requests (a failed read is recorded as
    none, so it lands here too). All of them render nothing rather than an empty
    card.

    A combination in flight is the exception. Answering the last outstanding
    request empties the list, so the table goes away while the work it started is
    still running, and the snackbar reporting that work has to outlive it.
  */
  if (
    !isFeatureLaunched ||
    rows === null ||
    (!hasRequests && combiningAccounts === null && !combined)
  ) {
    return null;
  }

  const statusLabel = (status: string) => {
    if (status === PENDING_STATUS) {
      return intl.getMessage(COMBINE_REQUESTS_STATUS_PENDING_KEY);
    }
    if (status === APPROVED_STATUS) {
      return intl.getMessage(COMBINE_REQUESTS_STATUS_APPROVED_KEY);
    }
    return status;
  };

  const renderAccountCell = (
    name: string | undefined,
    id: string | undefined,
  ) => (
    <Flex direction="column" rowGap="xxxsmall">
      <Text>{name || ABSENT_VALUE}</Text>
      <Text>{id || ABSENT_VALUE}</Text>
    </Flex>
  );

  const renderRows = (
    invitations: CombinationInvitationSummary[],
    actions: RowAction[],
  ) =>
    invitations.map((invitation) => (
      <tr
        key={invitation.accountCombinationInvitationId}
        data-testid={`combine-request-row-${invitation.accountCombinationInvitationId}`}
      >
        {/*
          Constant on every row rather than read from the response. An upgrade
          creates no invitation at all, so a combination is the only kind of
          request that can appear in this table.
        */}
        <td>
          <Text>{intl.getMessage(COMBINE_REQUESTS_TYPE_COMBINE_KEY)}</Text>
        </td>
        <td>
          {renderAccountCell(
            invitation.dspAccountName,
            invitation.accountIdsToCombine[0],
          )}
        </td>
        <td>
          {renderAccountCell(
            invitation.sponsoredAdsAccountName,
            invitation.advertiserAccountId,
          )}
        </td>
        <td>
          <StatusPill $approved={invitation.status === APPROVED_STATUS}>
            {statusLabel(invitation.status)}
          </StatusPill>
        </td>
        <td>
          <Text>{formatRequestDate(invitation.requestedOn, locale)}</Text>
        </td>
        <td>
          <Text>{formatRequestDate(invitation.expiresOn, locale)}</Text>
        </td>
        <td>
          {invitation.status === APPROVED_STATUS ? (
            renderApprovedAction(invitation)
          ) : (
            <Dropdown
              /*
                The label is fixed rather than reflecting a selection: this is a menu
                of actions, not a value to choose, so it must still read "Actions"
                after one has been taken.
              */
              label={intl.getMessage(COMBINE_REQUESTS_ACTIONS_LABEL_KEY)}
              hideLabel
              selectedLabelOverride={intl.getMessage(
                COMBINE_REQUESTS_ACTIONS_LABEL_KEY,
              )}
              small
              onChange={(value) => {
                const action = actions.find((entry) => entry.status === value);
                if (action) {
                  if (
                    action.status === CombinationInvitationUpdateStatus.APPROVED
                  ) {
                    setConfirmChecked(false);
                    setConfirming(invitation);
                    return;
                  }
                  answer(invitation, action.status);
                }
              }}
              data-testid={`combine-request-actions-${invitation.accountCombinationInvitationId}`}
            >
              {actions.map((action) => (
                <DropdownItem key={action.labelKey} value={action.status}>
                  {intl.getMessage(action.labelKey)}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
        </td>
      </tr>
    ));

  /*
    An approved request offers no actions: it has already been answered, and neither
    approving nor declining it again means anything. What it offers instead is the way
    into what the approval produced, matching what a completed combination does.

    The link needs an entity and that account's host, and the invitation summary carries
    neither today, so it falls back to the same dash the other unavailable fields use
    rather than pointing campaign manager at whichever account it defaults to.
  */
  /*
    Leaves the dialog if there is somewhere to go, and closes it if there is not. A row whose
    combined account has not been created yet carries neither identifier, so its destination
    cannot be built, and dismissing is the honest outcome: the alternative is opening whichever
    account the destination defaults to, moments after telling someone this one was combined.
  */
  const navigateOrDismiss = (destination: string | undefined) => {
    setCombined(null);
    if (destination) {
      window.location.assign(destination);
    }
  };

  const renderApprovedAction = (invitation: CombinationInvitationSummary) => {
    const campaignsUrl = ucmAllCampaignsUrl(
      invitation.regionalEntityId,
      invitation.url,
    );
    if (!campaignsUrl) {
      return <Text>{ABSENT_VALUE}</Text>;
    }
    return (
      <Link href={campaignsUrl}>
        {intl.getMessage(COMBINE_REQUESTS_VIEW_CAMPAIGNS_KEY)}
      </Link>
    );
  };

  const renderTable = (
    invitations: CombinationInvitationSummary[],
    actions: RowAction[],
  ) => {
    return (
      <>
        <Table>
          <thead>
            <tr>
              <th scope="col">
                <Flex alignItems="center" columnGap="xxxsmall">
                  <Text>
                    {intl.getMessage(COMBINE_REQUESTS_COLUMN_REQUEST_TYPE_KEY)}
                  </Text>
                  <HelpTip position="top">
                    <Flex direction="column" rowGap="small">
                      <Text>
                        {intl.getMessage(
                          COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_INTRO_KEY,
                        )}
                      </Text>
                      <Text>
                        <strong>
                          {intl.getMessage(
                            COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_COMBINE_HEADER_KEY,
                          )}
                        </strong>
                        <br />
                        {intl.getMessage(
                          COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_COMBINE_BODY_KEY,
                        )}
                      </Text>
                      <Text>
                        <strong>
                          {intl.getMessage(
                            COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_UPGRADE_HEADER_KEY,
                          )}
                        </strong>
                        <br />
                        {intl.getMessage(
                          COMBINE_REQUESTS_REQUEST_TYPE_TOOLTIP_UPGRADE_BODY_KEY,
                        )}
                      </Text>
                    </Flex>
                  </HelpTip>
                </Flex>
              </th>
              <th scope="col">
                <Text>
                  {intl.getMessage(COMBINE_REQUESTS_COLUMN_DSP_ACCOUNT_KEY)}
                </Text>
              </th>
              <th scope="col">
                <Text>
                  {intl.getMessage(COMBINE_REQUESTS_COLUMN_SA_ACCOUNT_KEY)}
                </Text>
              </th>
              <th scope="col">
                <Text>
                  {intl.getMessage(COMBINE_REQUESTS_COLUMN_STATUS_KEY)}
                </Text>
              </th>
              <th scope="col">
                <Text>
                  {intl.getMessage(COMBINE_REQUESTS_COLUMN_REQUESTED_ON_KEY)}
                </Text>
              </th>
              <th scope="col">
                <Text>
                  {intl.getMessage(COMBINE_REQUESTS_COLUMN_EXPIRES_ON_KEY)}
                </Text>
              </th>
              <th scope="col">
                <Text>
                  {intl.getMessage(COMBINE_REQUESTS_COLUMN_ACTION_KEY)}
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>{renderRows(invitations, actions)}</tbody>
        </Table>
      </>
    );
  };

  const isSentTab = selectedTab === CombinationInvitationDirection.SENT;

  const overlays = (
    <>
      {confirming && (
        <ConfirmationModal
          isOpen
          hasPendingAccess={false}
          selectedItems={[]}
          confirmChecked={confirmChecked}
          onConfirmCheckedChange={setConfirmChecked}
          onClose={() => setConfirming(null)}
          onConfirm={() => approve(confirming)}
          header={intl.getMessage(COMBINE_REQUESTS_CONFIRM_TITLE_KEY)}
          bodyText={intl.getMessage(COMBINE_REQUESTS_CONFIRM_BODY_KEY)}
          bullets={[
            COMBINE_REQUESTS_CONFIRM_BULLET_BILLING_KEY,
            COMBINE_REQUESTS_CONFIRM_BULLET_CAMPAIGNS_KEY,
            COMBINE_REQUESTS_CONFIRM_BULLET_ADMINS_KEY,
            COMBINE_REQUESTS_CONFIRM_BULLET_FEATURES_KEY,
            COMBINE_REQUESTS_CONFIRM_BULLET_MANAGE_KEY,
          ].map((key) => intl.getMessage(key))}
          /*
            The shared link rather than a plain one: this sits inside the
            checkbox's own label, where any bubbling click toggles the checkbox,
            so a plain anchor would make reading the agreement accept it.
          */
          /*
            Keyed because formatMessage splices this into an array of children around
            the sentence's text, and React asks for a key on anything it renders from a
            list. Without one it renders correctly and warns on every open.
          */
          checkboxLabel={intl.formatMessage(
            COMBINE_REQUESTS_CONFIRM_AGREEMENT_KEY,
            { agreement: <AmazonAdsAgreementLink key="agreement" /> },
          )}
          submitLabel={intl.getMessage(COMBINE_REQUESTS_CONFIRM_SUBMIT_KEY)}
          cancelLabel={intl.getMessage(COMBINE_ACCOUNTS_CANCEL_KEY)}
          confirmBodyWidth={CONFIRM_BODY_WIDTH}
          submitting={
            submitting
              ? { label: intl.getMessage(COMBINE_REQUESTS_CONFIRM_LOADING_KEY) }
              : undefined
          }
          // The requester's terminal frame belongs to the other lane; this caller
          // never reaches it, so its copy is supplied as empty rather than invented.
          pendingHeader=""
          pendingBodyText=""
          pendingEmailNote=""
          doneLabel=""
        />
      )}

      {combiningAccounts !== null && (
        /*
          Floated rather than rendered in place, matching the upgrade notifier on this same
          page: Storm's Snackbar has no positioning of its own, so inline it lands in the
          page flow inside the card instead of bottom right where the design puts it.
        */
        <div style={SNACKBAR_POSITION}>
          <Snackbar
            id="combine-requests-progress"
            /* Carries the spinner the design shows, since the work is still running. */
            variant="loading"
            withCloseButton
            /*
              Dismissing hides the notification, it does not stop the watch: the
              combination continues server-side either way, so the close button dismisses
              a message rather than cancelling something already accepted.
            */
            onClose={() => setCombiningAccounts(null)}
            data-testid="combine-requests-progress-snackbar"
          >
            {combiningAccounts === 1
              ? intl.getMessage(COMBINE_REQUESTS_PROGRESS_SNACKBAR_ONE_KEY)
              : intl.formatMessage(COMBINE_REQUESTS_PROGRESS_SNACKBAR_KEY, {
                  count: combiningAccounts,
                })}
          </Snackbar>
        </div>
      )}

      {combined !== null && (
        <Modal
          isOpen
          header={intl.getMessage(COMBINE_REQUESTS_SUCCESS_TITLE_KEY)}
          onClose={() => setCombined(null)}
          /*
            Each destination is built from the refreshed row, which carries the combined
            account's entity and marketplace once that account has been created. A row
            still missing either leaves its button dismissing and nothing more, rather
            than sending someone to whichever account the destination defaults to.
          */
          footer={
            <ModalFooterActions data-testid="combine-requests-success-footer">
              <Button
                onClick={() =>
                  navigateOrDismiss(
                    userManagementUrl(combined.regionalEntityId, combined.url),
                  )
                }
              >
                {intl.getMessage(COMBINE_REQUESTS_SUCCESS_ACCOUNT_SETTINGS_KEY)}
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  navigateOrDismiss(
                    ucmAllCampaignsUrl(combined.regionalEntityId, combined.url),
                  )
                }
              >
                {intl.getMessage(COMBINE_REQUESTS_SUCCESS_CAMPAIGNS_KEY)}
              </Button>
            </ModalFooterActions>
          }
        >
          <Text>{intl.getMessage(COMBINE_REQUESTS_SUCCESS_BODY_KEY)}</Text>
        </Modal>
      )}
    </>
  );

  // The table can be absent while an overlay is not: answering the last request
  // empties the list before the work it started has finished.
  if (!hasRequests) {
    return overlays;
  }

  return (
    <>
      <Box
        paddingBlockEnd="medium"
        id={PENDING_REQUESTS_CARD_ID}
        data-testid={PENDING_REQUESTS_CARD_ID}
      >
        <Card>
          <CardHeader>
            {/*
              Stated rather than left to CardHeader's own styling, because the design
              gives this the display typeface at display/medium, which is what Heading
              carries. h4 sits one level under the page's own h3, so the card reads as
              a section of the page rather than a peer of it, and a screen reader can
              reach the table through the heading outline.
            */}
            <Heading renderAs="h4" typeface="display" size="medium">
              {intl.getMessage(COMBINE_REQUESTS_HEADING_KEY)}
            </Heading>
          </CardHeader>
          {/*
          Both tabs are always offered while the card is on screen, even when one
          of them is empty: a user who has sent a request and received none still
          needs to see that receiving none is the state, rather than have the tab
          disappear and take that information with it.
        */}
          <TabbedNavigation tablist>
            <TabbedNavigationItem
              label={intl.formatMessage(COMBINE_REQUESTS_TAB_RECEIVED_KEY, {
                count: received.length,
              })}
              active={!isSentTab}
              onClick={() =>
                setSelectedTab(CombinationInvitationDirection.RECEIVED)
              }
              data-testid="combine-requests-tab-received"
            />
            <TabbedNavigationItem
              label={intl.formatMessage(COMBINE_REQUESTS_TAB_SENT_KEY, {
                count: sent.length,
              })}
              active={isSentTab}
              onClick={() =>
                setSelectedTab(CombinationInvitationDirection.SENT)
              }
              data-testid="combine-requests-tab-sent"
            />
          </TabbedNavigation>

          {actionFailed && (
            <Box paddingBlockStart="small">
              <Alert
                variant="error"
                isOpen
                data-testid="combine-requests-action-error"
              >
                {intl.getMessage(COMBINE_REQUESTS_ACTION_ERROR_KEY)}
              </Alert>
            </Box>
          )}

          {isSentTab
            ? renderTable(sent, SENT_ACTIONS)
            : renderTable(received, RECEIVED_ACTIONS)}
        </Card>
      </Box>
      {overlays}
    </>
  );
};

export default PendingRequestsCard;
